import { dist2, idiv, imax, isqrt, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import { castSkill } from '../battle/skills.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { patchAt, patchNear, summerOrder } from '../battle/druid-summer.ts';
import { useSkills } from './ghost.ts';

/**
 * The druid's chapter 2 played by the lab's bot (docs/SENDAS.md, «Calibración»; ai/senda-bot.ts sends it here):
 *
 * - the kilns (`kilns`): who knows strikes first the wyvern diving at a kiln (it pulls up), then what is at Hob, keeps
 *   by him and steps away from a kiln that cracks; who doesn't fights the nearest wherever it stands.
 * - Olmo's pine (`strip`): who knows keeps by Hob above the fire's line, steps out from under an ember's white smoke and
 *   out of the burning ground, strikes what is at Hob first; who doesn't fights the nearest.
 * - the counterfire (`counter`, the test): who knows lights the patches just above the pine early (the wind takes them
 *   up, away), and when the wind has turned the ones just below it (now they go down, against the great fire); strikes
 *   the old wyvern while it dives, steps off its breath's line and out of what burns. Who doesn't cuts the great fire at
 *   once (the patches under the pine and the band under them, first thing), then the ones above.
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

const guard = (s: BattleState, h: Hero, x: number, y: number, r: number): boolean => {
  const t = nearestTo(
    enemiesNear(s, x, y, r, (e) => targetable(s, e)),
    x,
    y,
  );
  if (!t) return false;
  strike(s, h, t);
  return true;
};

const hobOf = (s: BattleState): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === 'hob');

/** Away from a point, to `d` from it (on floor). */
const away = (s: BattleState, h: Hero, x: number, y: number, d: number): void => {
  const dx = h.x - x;
  const dy = h.y - y;
  const n = imax(1, isqrt(dx * dx + dy * dy));
  let tx = x + idiv(dx * d, n);
  let ty = y + idiv(dy * d, n);
  if (!heroWalkable(s, cellAtWorld(s, tx, ty))) {
    tx = x - idiv(dy * d, n);
    ty = y + idiv(dx * d, n);
  }
  h.target = 0;
  go(s, h, tx, ty, 150);
};

/** A flock of crows on the hero (three or more close): the nearest of them. */
const flockOn = (s: BattleState, h: Hero, n = 3): Enemy | null => {
  const flock = enemiesNear(s, h.x, h.y, 1400, (e) => targetable(s, e) && e.kind === 'crow');
  return flock.length >= n ? nearestTo(flock, h.x, h.y) : null;
};

/** The wyvern diving now (the kilns' or the old one), if any. */
const diver = (s: BattleState): Enemy | null => {
  const w = s.senda?.summer;
  if (!w) return null;
  for (const id of Object.keys(w.wy)) {
    const m = w.wy[Number(id)] as number[];
    if (m[0] !== 1) continue;
    const e = findEnemy(s, Number(id));
    if (e && e.hp > 0) return e;
  }
  return null;
};

/* ---------- the kilns ---------- */

const kilnsBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.summer?.kilns;
  const w = s.senda?.summer;
  if (!r || !w) return;
  const hob = hobOf(s);
  if (!knows) {
    if (guard(s, h, h.x, h.y, 7000)) return;
    h.target = 0;
    if (hob) go(s, h, hob.x + 900, hob.y + 600);
    return;
  }
  // Out of a kiln that cracks.
  for (let k = 0; k < w.kiln.length; k++) {
    if (w.kiln[k] !== 2) continue;
    const p = r.at[k] as { x: number; y: number };
    if (withinRange(h.x, h.y, p.x, p.y, r.burstR + 500)) return away(s, h, p.x, p.y, r.burstR + 900);
  }
  // A flock on the hero: those first (they outrun it); then the dive.
  // The dive close by first; then any crow on the hero (they outrun it: running after a far dive only feeds them).
  const d = diver(s);
  if (d && withinRange(d.x, d.y, h.x, h.y, 4500)) return strike(s, h, d);
  // The crows from afar, before they get there (they outrun the hero).
  const crows = enemiesNear(s, h.x, h.y, 6000, (e) => targetable(s, e) && e.kind === 'crow');
  if (crows.length) return strike(s, h, nearestTo(crows, h.x, h.y) as Enemy);
  if (d && withinRange(d.x, d.y, h.x, h.y, 7000)) return strike(s, h, d);
  if (hob && hob.hp > 0 && guard(s, h, hob.x, hob.y, 2400)) return;
  if (guard(s, h, h.x, h.y, 3200)) return;
  // A wyvern about: at it, from where Hob is.
  for (const id of Object.keys(w.wy)) {
    const e = findEnemy(s, Number(id));
    if (e && e.hp > 0 && targetable(s, e) && withinRange(e.x, e.y, h.x, h.y, 7000)) return strike(s, h, e);
  }
  h.target = 0;
  if (hob) go(s, h, hob.x + 1000, hob.y + 900);
};

/* ---------- Olmo's pine ---------- */

const stripBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.summer?.strip;
  const w = s.senda?.summer;
  if (!r || !w) return;
  const hob = hobOf(s);
  if (!knows) {
    if (guard(s, h, h.x, h.y, 7000)) return;
    h.target = 0;
    if (hob) go(s, h, hob.x + 900, hob.y + 900);
    return;
  }
  // Above the fire's line.
  if (h.y > w.front - 1200) {
    h.target = 0;
    return go(s, h, h.x, w.front - 2600, 200);
  }
  // Out from under an ember (falling or burning).
  for (const e of w.embers) {
    if (s.tick < e.until && withinRange(h.x, h.y, e.x, e.y, r.ember.r + 450)) return away(s, h, e.x, e.y, r.ember.r + 900);
  }
  const flock = flockOn(s, h);
  if (flock) return strike(s, h, flock);
  if (hob && hob.hp > 0 && guard(s, h, hob.x, hob.y, 2400)) return;
  if (guard(s, h, h.x, h.y, 3400)) return;
  h.target = 0;
  if (hob) go(s, h, hob.x + 1100, imax(hob.y - 900, imax(r.line - 3000, hob.y - 900)));
};

/* ---------- the counterfire ---------- */

const counterBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.summer?.counter;
  const w = s.senda?.summer;
  const st = w?.fire;
  if (!r || !w || !st || w.done >= 0) return;
  // Mid-throw: let it be.
  if (w.job >= 0 && w.held > 0) return;
  const boss = w.boss ? findEnemy(s, w.boss) : null;
  if (knows) {
    // Off the breath's line.
    if (w.bt >= 0) {
      const dx = w.bx2 - w.bx;
      const dy = w.by2 - w.by;
      const n = imax(1, isqrt(dx * dx + dy * dy));
      // Square off the line, on the side it already leans to: from the hero's foot on the line (the same point every
      // tick while it goes straight out, so the order holds).
      const along = idiv((h.x - w.bx) * dx + (h.y - w.by) * dy, n);
      const fx = w.bx + idiv(dx * along, n);
      const fy = w.by + idiv(dy * along, n);
      const side = (h.x - w.bx) * dy - (h.y - w.by) * dx >= 0 ? 1 : -1;
      let tx = fx + idiv(dy * side * 2000, n);
      let ty = fy + idiv(-dx * side * 2000, n);
      if (!heroWalkable(s, cellAtWorld(s, tx, ty))) {
        tx = fx - idiv(dy * side * 2000, n);
        ty = fy + idiv(dx * side * 2000, n);
      }
      w.job = -1;
      h.target = 0;
      return go(s, h, tx, ty, 400);
    }
    // Out of what burns.
    const here = patchAt(s, h.x, h.y);
    if (here >= 0 && st.st[here] === 1) {
      w.job = -1;
      h.target = 0;
      return go(s, h, r.pine.x + (h.x > r.pine.x ? 1600 : -1600), r.pine.y, 200);
    }
    // The old wyvern diving: strike it; a flock on the hero before anything else that can wait.
    const d = diver(s);
    const flock = flockOn(s, h);
    if (flock && !d && w.job < 0) return strike(s, h, flock);
    if (d) {
      w.job = -1;
      return strike(s, h, d);
    }
    // The plan: above early (black before the turn), below once the wind blows down.
    if (w.job < 0) {
      const t = w.t;
      const down = t >= r.flip;
      const todo = (down ? r.net.pineDown : t < r.flip - r.burn - 36 ? r.net.pineUp : []).filter((i) => st.st[i] === 0);
      if (todo.length) {
        let best = todo[0] as number;
        let bd = 0x7fffffff;
        for (const i of todo) {
          const p = patchNear(r, i, h.x, h.y);
          const dd = dist2(p.x, p.y, h.x, h.y);
          if (dd < bd) {
            bd = dd;
            best = i;
          }
        }
        return summerOrder(s, h, best);
      }
    } else return;
    if (guard(s, h, h.x, h.y, 2600)) return;
    if (boss && boss.hp > 0 && targetable(s, boss)) return strike(s, h, boss);
    h.target = 0;
    return go(s, h, r.pine.x + 1500, r.pine.y);
  }
  // Who doesn't: cut the great fire at once, the ones under the pine and the band under them; then the ones above.
  if (w.job >= 0) return;
  const below = r.net.cells.map((_, i) => i).filter((i) => r.net.band[i] === (r.net.band[r.net.pineDown[0] as number] as number) + 1);
  const todo = [...r.net.pineDown, ...below, ...r.net.pineUp].filter((i) => st.st[i] === 0);
  if (todo.length) return summerOrder(s, h, todo[0] as number);
  if (guard(s, h, h.x, h.y, 7000)) return;
  if (boss && boss.hp > 0 && targetable(s, boss)) return strike(s, h, boss);
};

export const summerBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.summer;
  if (!r) return;
  if (r.kilns) return kilnsBot(s, h, knows);
  if (r.strip) return stripBot(s, h, knows);
  if (r.counter) return counterBot(s, h, knows);
};
